import React, { useContext } from 'react';

import { OrderTypes } from '@amzn/awsscmui-argo-inventory-exception/common/main';
import { Box, BoxProps, Icon, Link, SpaceBetween } from '@amzn/awsui-components-react-v3';

import { argoValues } from '../../argoConfig';
import { boostConfig } from '../../boost/utils';
import { Context } from '../../store';
import { AppType } from '../../store/appContext';
import { Model, ReceiveType, ReceiveTypeNameMap, ReferenceQueryResponseItem } from '../../store/types';

interface ValueWithLabelProps {
  label: string | React.ReactNode;
  children: React.ReactNode;
  className?: string;
  inactive?: boolean;
  color?: BoxProps.Color;
}

interface getMappedFieldsProps {
  model: Model;
}

interface GetExeceptionURLProps {
  referenceId?: string;
  path?: string;
  title?: string;
  appType?: AppType;
  target?: string;
  withLink?: boolean;
}

const UNIFIED_RECEIVE_DOMAIN: { [key: string]: string } = {
  alpha: 'scm-portal-alpha-pdx.aka.amazon.com',
  beta: 'scm-portal-beta-pdx.aka.amazon.com',
  betazhy: 'scm-beta-zhy.argo.ocean-wave.aws.a2z.org.cn',
  prod: 'aws.argo.ocean-wave.aws.a2z.com',
  prodzhy: 'aws.argo.ocean-wave.aws.a2z.org.cn',
};

const EXCEPTION_URLS: { [key: string]: string } = {
  alpha: `https://${UNIFIED_RECEIVE_DOMAIN.alpha}/assets-inventory/receive/exception`,
  beta: `https://${UNIFIED_RECEIVE_DOMAIN.beta}/assets-inventory/receive/exception`,
  prod: `https://${UNIFIED_RECEIVE_DOMAIN.prod}/assets-inventory/receive/exception`,
  // ZHY Links
  betazhy: `https://${UNIFIED_RECEIVE_DOMAIN.betazhy}/assets-inventory/receive/exception`,
  prodzhy: `https://${UNIFIED_RECEIVE_DOMAIN.prodzhy}/assets-inventory/receive/exception`,
};

const MOBILITY_DOMAIN: { [key: string]: string } = {
  alpha: 'mobility-beta.amazon.com',
  beta: 'mobility-beta.amazon.com',
  betazhy: 'mobility-beta.zhy.aws-border.cn',
  prod: 'mobility.amazon.com',
  prodzhy: 'mobility.aws-border.cn',
};

const CURRENT_RECEIVE_DOMAIN: { [key: string]: string } = {
  alpha: 'datacenter-alpha-pdx.aka.amazon.com',
  beta: 'datacenter-beta-pdx.aka.amazon.com',
  betazhy: 'mobility-beta.zhy.aws-border.cn/part/receive', // CN customers currently receive directly via Mobility.
  prod: 'datacenter-pdx.aka.amazon.com',
  prodzhy: 'mobility.aws-border.cn/part/receive', // CN customers currently receive directly via Mobility.
};

const IA_PERMISSIONS_URL: { [key: string]: string } = {
  alpha: 'https://permissions.amazon.com/a/team/receive-ia-tool-access',
  beta: 'https://permissions.amazon.com/a/team/receive-ia-tool-access',
  betazhy: 'https://permissions.amazon.com/a/team/operatornet-receive-ia-uat-cn',
  prod: 'https://permissions.amazon.com/a/team/receive-tool-ia-argo-users-prod',
  prodzhy: 'https://permissions.amazon.com/a/team/operatornet-receive-ia-prod-cn',
};

export const ValueWithLabel = ({ label, children, className = '', color = 'inherit' }: ValueWithLabelProps) => (
  <div className={`value-with-label ${className}`}>
    <Box variant="awsui-key-label" color={color}>
      {label}
    </Box>
    {children}
  </div>
);

export const getConfig = (appType?: AppType) => {
  if (!appType || appType === AppType.ARGO) {
    return argoValues;
  }

  return boostConfig();
};

export const isZhyRegion = (appType?: AppType) => {
  const context = getConfig(appType);
  const region = context?.region;
  return region === 'cn-northwest-1' || region === 'zhy';
};

export const getStage = (appType?: AppType) => {
  const context = getConfig(appType);
  if (isZhyRegion(appType)) {
    return context?.frameworkStage + 'zhy';
  } else {
    return context?.frameworkStage;
  }
};

export const getMobilityUrl = (appType?: AppType) => {
  const stage: string = getStage(appType);
  const domain = MOBILITY_DOMAIN?.[stage];
  return `https://${domain}/part/search?`;
};

export const getCurrentReceiveUrl = (appType?: AppType) => {
  const stage: string = getStage(appType);
  const domain = CURRENT_RECEIVE_DOMAIN?.[stage];
  return `https://${domain}/`;
};

export const getUnifiedReceiveUrl = (appType?: AppType) => {
  const stage: string = getStage(appType);
  const domain = UNIFIED_RECEIVE_DOMAIN?.[stage];
  return `https://${domain}/inventory-receive/receive/`;
};

export const GetIAPermissionsURL = (appType?: AppType) => {
  return IA_PERMISSIONS_URL?.[getStage(appType)];
};

export const getBaseExceptionUrl = (appType?: AppType) => {
  const stage: string = getStage(appType);

  const cellExists = EXCEPTION_URLS?.[stage];

  return cellExists || EXCEPTION_URLS.alpha;
};

export const GetExeceptionURL = ({
  referenceId,
  path,
  title,
  appType,
  target = '_self',
  withLink = true,
}: GetExeceptionURLProps) => {
  const { state } = useContext(Context);

  const URL = getBaseExceptionUrl(appType);

  const getExceptionUrl = () => {
    const receiveReference: ReferenceQueryResponseItem = state?.receiveReferences?.find(
      (ref: ReferenceQueryResponseItem) =>
        ref?.receiveReference.referenceId === referenceId || ref?.receiveReference.receiveReferenceId === referenceId,
    );
    const shipmentId = receiveReference?.receiveReference?.externalReferenceValue;
    const trackingNumber = receiveReference?.receiveReference?.trackingId;

    const urlWithShipmentId = `${URL}${shipmentId}`;

    return trackingNumber ? `${urlWithShipmentId}&trackingId=${trackingNumber}` : urlWithShipmentId;
  };

  const execeptionURL = !referenceId ? URL : getExceptionUrl();

  if (!withLink) {
    return `${execeptionURL}/${path}`;
  }

  return (
    <Link href={`${execeptionURL}/${path}`} target={target}>
      <SpaceBetween size="xs" direction="horizontal">
        <Icon name="external" />
        <span>{title}</span>
      </SpaceBetween>
    </Link>
  );
};

export const getOrderType = (model: Model) => {
  if (!model) {
    return null;
  }

  switch (model?.externalReferenceType) {
    case ReceiveType.ASN_JANUS: {
      return ReceiveTypeNameMap[ReceiveType.ASN_JANUS];
    }
    case ReceiveType.ASN_AWS_OWNED_INVENTORY_RO: {
      return ReceiveTypeNameMap[ReceiveType.ASN_AWS_OWNED_INVENTORY_RO];
    }
    case ReceiveType.ASN_RMA: {
      return ReceiveTypeNameMap[ReceiveType.ASN_RMA];
    }
    case ReceiveType.ASN_WW_FA: {
      return ReceiveTypeNameMap[ReceiveType.ASN_WW_FA];
    }
    case ReceiveType.ASN_OMD: {
      return ReceiveTypeNameMap[ReceiveType.ASN_OMD];
    }
    case ReceiveType.AWS_PO: {
      return ReceiveTypeNameMap[ReceiveType.AWS_PO];
    }
    case ReceiveType.ASN_AWS_PO: {
      return ReceiveTypeNameMap[ReceiveType.ASN_AWS_PO];
    }
    default:
      return null;
  }
};

export const getOrderTypeForException = (orderType: string) => {
  if (!orderType) {
    return null;
  }

  switch (orderType) {
    case ReceiveType.AWS_PO: {
      return OrderTypes.PO;
    }
    case ReceiveType.ASN_AWS_PO: {
      return OrderTypes.ASN_PO;
    }
    case ReceiveType.ASN_RMA: {
      return OrderTypes.RMA;
    }
    case ReceiveType.ASN_WW_FA: {
      return OrderTypes.FA;
    }
    case ReceiveType.ASN_OMD: {
      return OrderTypes.OMD;
    }
    case ReceiveType.ASN_JANUS: {
      return OrderTypes.JANUS;
    }
    default:
      return null;
  }
};

export const getOrderNumber = ({ model }: getMappedFieldsProps) => {
  if (!model) {
    return null;
  }

  switch (model?.externalReferenceType) {
    case ReceiveType.ASN_JANUS: {
      return model.requisitionId;
    }
    case ReceiveType.ASN_AWS_OWNED_INVENTORY_RO: {
      return model.requisitionId;
    }
    case ReceiveType.ASN_RMA: {
      return model.rmaID;
    }
    case ReceiveType.ASN_WW_FA: {
      return model.wwFaId;
    }
    default:
      return model.orderNumber;
  }
};

export const getOrderLineItemNumber = ({ model }: getMappedFieldsProps) => {
  if (!model) {
    return null;
  }

  switch (model.externalReferenceType) {
    case ReceiveType.ASN_JANUS: {
      return model.requisitionLineItemNumber;
    }
    case ReceiveType.ASN_AWS_OWNED_INVENTORY_RO: {
      return model.requisitionLineItemNumber;
    }
    case ReceiveType.ASN_RMA: {
      return model.rmaLineItemNumber;
    }
    case ReceiveType.ASN_WW_FA: {
      return model.wwFaLineItemNumber;
    }
    default:
      return model.orderLineItemNumber;
  }
};

export const isNetworkOffline = !window.navigator.onLine;

export const sortByLabel = (l1, l2) => {
  const label1 = l1.label?.toUpperCase();
  const label2 = l2.label?.toUpperCase();
  if (label2 > label1) {
    return -1;
  }
  if (label2 > label1) {
    return 1;
  }
  return 0;
};

export const TagOptions = [
  { label: 'Last Time Buy', id: 'Last Time Buy' },
  { label: 'HIPPO REWORK PROJECT', id: 'HIPPO REWORK PROJECT' },
  { label: 'Recovered Gear', id: 'Recovered Gear' },
  { label: 'Late Binding Optics', id: 'Late Binding Optics' },
  { label: 'Rework project', id: 'Rework project' },
  { label: 'Quarantine', id: 'Quarantine' },
  { label: 'RMA-Inbound', id: 'RMA-Inbound' },
  { label: 'BUILD', id: 'BUILD' },
  { label: 'ATS', id: 'ATS' },
  { label: 'WW Decom Analysis', id: 'WW Decom Analysis' },
  { label: 'OOW', id: 'OOW' },
  { label: 'DCOSE-10043', id: 'DCOSE-10043' },
  { label: 'NetScaling', id: 'NetScaling' },
  { label: 'HDD Removed', id: 'HDD Removed' },
  { label: 'Failure Analysis', id: 'Failure Analysis' },
  { label: 'Damaged', id: 'Damaged' },
  { label: 'ADVANCED RMA', id: 'ADVANCED RMA' },
  { label: 'Late Binding', id: 'Late Binding' },
  { label: 'Seed Stock', id: 'Seed Stock' },
  { label: 'CRITICAL SPARES', id: 'CRITICAL SPARES' },
  { label: 'Console Host Retrofit', id: 'Console Host Retrofit' },
  { label: 'Priority RMA', id: 'Priority RMA' },
  { label: 'SNOWBALL', id: 'SNOWBALL' },
  { label: 'SD Card', id: 'SD Card' },
  { label: 'TOA', id: 'TOA' },
  { label: 'Reconciling', id: 'Reconciling' },
  { label: 'MVP ITAR SPARES', id: 'MVP ITAR SPARES' },
  { label: 'GPS/PIE', id: 'GPS/PIE' },
  { label: 'SD Card Removed', id: 'SD Card Removed' },
  { label: 'BUILD_TO_DCO', id: 'BUILD_TO_DCO' },
  { label: 'STORM RACK', id: 'STORM RACK' },
  { label: 'Cloudfront', id: 'Cloudfront' },
  { label: 'RMA PROCESSED', id: 'RMA PROCESSED' },
  { label: 'DOA', id: 'DOA' },
  { label: 'SSD Removed', id: 'SSD Removed' },
  { label: 'Switch - SD card destroyed', id: 'Switch - SD card destroyed' },
  { label: 'NW - Thermal Event/Failure', id: 'NW - Thermal Event/Failure' },
  { label: 'SECURITY', id: 'SECURITY' },
  { label: 'S3 project', id: 'S3 project' },
  { label: 'EAGLE_EYE', id: 'EAGLE_EYE' },
  { label: 'SPEAR Media', id: 'SPEAR Media' },
]
  .map((option) => ({ ...option, value: option.id }))
  .sort(sortByLabel);

export const AsnTypes = [
  ReceiveType.ASN_JANUS,
  ReceiveType.ASN_OMD,
  ReceiveType.ASN_RMA,
  ReceiveType.ASN_WW_FA,
  ReceiveType.ASN_AWS_OWNED_INVENTORY_RO,
];
export const isASNType = (orderType: ReceiveType) => AsnTypes.includes(orderType);

export const DEFAULT_TABLE_PAGE_SIZE = 100;
export const DEFAULT_TABLE_TOTAL_ROWS = 20;

export const DEFAULT_TOTAL_PAGES = DEFAULT_TABLE_PAGE_SIZE / DEFAULT_TABLE_TOTAL_ROWS;
export const DEFAULT_TABLE_ROWS_PER_PAGE = DEFAULT_TABLE_PAGE_SIZE / DEFAULT_TOTAL_PAGES;

export const EXECUTION_WAIT_TIME = 3600;

export const sortByLastThree = (node1: any, node2: any, sortBy: string) => {
  if (node1?.[sortBy]?.slice(-3) < node2?.[sortBy]?.slice(-3)) return 1;
  if (node1?.[sortBy]?.slice(-3) > node2?.[sortBy]?.slice(-3)) return -1;
  return 0;
};
